<script setup lang="ts">
import { usePortfolioStore } from "@/stores/portfolio";
import { defineProps, computed, useAttrs } from "vue";
import {
  ArrowPathRoundedSquareIcon,
  CodeBracketSquareIcon,
  CloudIcon,
} from "@heroicons/vue/24/solid";
import Skills from "../Skills.vue";
import PrintableSkills from "../PrintableSkills.vue";
import {
  DELIVERY_SKILLS,
  PLATFORM_SKILLS,
  SOFTWARE_SKILLS,
} from "./skillCategories";

// Props definition
export interface Props {
  sectionClass?: string;
}

const { sectionClass } = defineProps<Props>();
const attrs = useAttrs();

// Use the portfolio store
const portfolioStore = usePortfolioStore();

// Mapping skill categories to icons and titles
const skillSections = computed(() => {
  const availableSkills = new Set(
    portfolioStore.allSkills.map((skill) => skill.label)
  );

  const availableFrom = (skills: string[]) =>
    skills.filter((skill) => availableSkills.has(skill));

  return [
    {
      title: "Platform Architecture",
      skills: availableFrom(PLATFORM_SKILLS),
      icon: CloudIcon,
    },
    {
      title: "Delivery & Reliability",
      skills: availableFrom(DELIVERY_SKILLS),
      icon: ArrowPathRoundedSquareIcon,
    },
    {
      title: "Software & Data",
      skills: availableFrom(SOFTWARE_SKILLS),
      icon: CodeBracketSquareIcon,
    },
  ];
});
</script>

<template>
  <div v-bind="attrs" id="mainSkills" class="grid gap-4 md:grid-cols-3">
    <!-- Iterate dynamically over skill sections -->
    <div
        v-for="section in skillSections"
        :key="section.title"
        :class="sectionClass"
        class="rounded-xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800/60"
    >
      <h3 class="flex items-center gap-2 text-base font-bold text-slate-950 dark:text-white">
        <span class="rounded-lg bg-cyan-100 p-2 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300">
          <component :is="section.icon" class="h-5 w-5" />
        </span>
        {{ section.title }}
      </h3>
      <Skills :skills="section.skills" class="screen-skill mt-4" />
      <PrintableSkills
          :skills="section.skills"
          class="hidden print:block text-xs text-left"
      />
    </div>
  </div>
</template>
